/**
 * Custom HTTP client for myVault API
 *
 * This client is used by Orval-generated API hooks and provides:
 * - Automatic JWT token injection
 * - RFC 7807 error handling
 * - Request/response transformation (camelCase <-> snake_case)
 * - Retry logic for transient failures
 */

// Types for API errors following RFC 7807
export interface ApiError {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
}

export class ApiException extends Error {
  public readonly status: number;
  public readonly type: string;
  public readonly detail?: string;
  public readonly errors?: Record<string, string[]>;

  constructor(error: ApiError) {
    super(error.title);
    this.name = 'ApiException';
    this.status = error.status;
    this.type = error.type;
    this.detail = error.detail;
    this.errors = error.errors;
  }
}

// Token management - to be implemented by auth module
let getAccessToken: (() => Promise<string | null>) | null = null;
let onTokenExpired: (() => void) | null = null;

export function setTokenProvider(provider: () => Promise<string | null>): void {
  getAccessToken = provider;
}

export function setTokenExpiredHandler(handler: () => void): void {
  onTokenExpired = handler;
}

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// NOTE: No case transformation is applied.
// API returns camelCase (via Pydantic's alias_generator=to_camel).
// OpenAPI spec is exported with camelCase property names.
// Orval generates camelCase TypeScript types that match runtime data.

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Request configuration from Orval
interface OrvalRequestConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  data?: unknown;
  signal?: AbortSignal;
}

/**
 * Custom fetch function for Orval-generated hooks
 * Accepts Orval's request config object format
 */
export async function customFetch<T>(
  config: OrvalRequestConfig
): Promise<T> {
  const { url, method, headers: customHeaders, params, data, signal } = config;

  // Build full URL with query params
  let fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    fullUrl += `?${searchParams.toString()}`;
  }

  // Get auth token if available
  let token: string | null = null;
  if (getAccessToken) {
    try {
      token = await getAccessToken();
    } catch {
      // Token retrieval failed, proceed without auth
    }
  }

  // Build headers
  const headers = new Headers(customHeaders);
  headers.set('Accept', 'application/json');

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Prepare request body if present
  let body: string | FormData | undefined;
  if (data !== undefined) {
    if (data instanceof FormData) {
      body = data;
      // Don't set Content-Type for FormData - browser will set it with boundary
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(data);
    }
  }

  // Retry loop
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(fullUrl, {
        method,
        headers,
        body,
        signal,
      });

      // Handle 401 - token expired
      if (response.status === 401 && onTokenExpired) {
        onTokenExpired();
        throw new ApiException({
          type: 'https://api.myvault.nl/errors/unauthorized',
          title: 'Unauthorized',
          status: 401,
          detail: 'Your session has expired. Please log in again.',
        });
      }

      // Handle error responses
      if (!response.ok) {
        let errorBody: ApiError;

        try {
          errorBody = await response.json();
        } catch {
          errorBody = {
            type: 'https://api.myvault.nl/errors/unknown',
            title: 'An error occurred',
            status: response.status,
            detail: response.statusText,
          };
        }

        // Check if retryable
        if (
          RETRYABLE_STATUS_CODES.includes(response.status) &&
          attempt < MAX_RETRIES
        ) {
          const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
          await sleep(delay);
          continue;
        }

        throw new ApiException(errorBody);
      }

      // Handle empty responses
      if (
        response.status === 204 ||
        response.headers.get('content-length') === '0'
      ) {
        return undefined as T;
      }

      // Parse response
      const rawData = await response.json();
      return rawData as T;
    } catch (error) {
      if (error instanceof ApiException) {
        throw error;
      }

      // Network errors - retry if possible
      if (
        error instanceof TypeError &&
        error.message.includes('fetch') &&
        attempt < MAX_RETRIES
      ) {
        lastError = error;
        const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
        await sleep(delay);
        continue;
      }

      throw error;
    }
  }

  // All retries exhausted
  throw lastError || new Error('Request failed after retries');
}
